/**
 * System Monitoring - Admin interface for system metrics and monitoring
 */

import React, { useState, useEffect } from 'react';
import { AdminService, SystemMetrics, AuditLogsResponse, SystemHealth } from '../../services/admin';
import { Card } from '../../components/ui/Card';
import { Alert } from '../../components/ui/Alert';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { Button } from '../../components/ui/Button';

export function SystemMonitoring() {
  const [metrics, setMetrics] = useState<SystemMetrics | null>(null);
  const [auditLogs, setAuditLogs] = useState<AuditLogsResponse | null>(null);
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filters for metrics and logs
  const [metricsHours, setMetricsHours] = useState(24);
  const [logsHours, setLogsHours] = useState(24);
  const [logsLimit, setLogsLimit] = useState(50);
  const [logFilter, setLogFilter] = useState<{
    action?: string;
    resource_type?: string;
    user_id?: number;
  }>({});

  useEffect(() => {
    loadMonitoringData();
    
    // Refresh every minute for real-time monitoring
    const interval = setInterval(loadMonitoringData, 60000);
    return () => clearInterval(interval);
  }, [metricsHours, logsHours, logsLimit, logFilter]);

  const loadMonitoringData = async () => {
    try {
      setError(null);
      
      // Load all monitoring data in parallel
      const [metricsResponse, logsResponse, healthResponse] = await Promise.all([
        AdminService.getSystemMetrics(metricsHours),
        AdminService.getAuditLogs({ hours: logsHours, limit: logsLimit, ...logFilter }),
        AdminService.getSystemHealth()
      ]);
      
      setMetrics(metricsResponse);
      setAuditLogs(logsResponse);
      setHealth(healthResponse);
    } catch (err) {
      console.error('Failed to load monitoring data:', err);
      setError('Failed to load monitoring data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const getActionDisplayName = (action: string) => {
    return action.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const getResourceIcon = (resourceType: string) => {
    switch (resourceType) {
      case 'user': return '👤';
      case 'lab': return '🧪';
      case 'run': return '🚀';
      case 'grade': return '📊';
      case 'config': return '⚙️';
      case 'system': return '🖥️';
      default: return '📄';
    }
  };

  const getHealthStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'text-green-400';
      case 'degraded': return 'text-yellow-400';
      case 'unhealthy': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  if (loading) {
    return (
      <div className="p-8 bg-gray-900 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-white mb-8">System Monitoring</h1>
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner size="lg" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 bg-gray-900 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white">System Monitoring</h1>
            <p className="text-gray-400 mt-1">
              Real-time system metrics, health status, and audit logs
            </p>
          </div>
          <div className="flex gap-4 items-center">
            <select
              value={metricsHours}
              onChange={(e) => setMetricsHours(Number(e.target.value))}
              className="bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white"
            >
              <option value={1}>Last Hour</option>
              <option value={6}>Last 6 Hours</option>
              <option value={24}>Last 24 Hours</option>
              <option value={168}>Last Week</option>
            </select>
            <Button variant="secondary" onClick={loadMonitoringData}>
              Refresh
            </Button>
          </div>
        </div>

        {error && (
          <Alert
            variant="error"
            title="Error"
            className="mb-6"
          >
            {error}
          </Alert>
        )}

        {/* System Health Overview */}
        <div className="mb-8">
          <Card className="p-6">
            <h2 className="text-xl font-semibold text-white mb-4">System Health</h2>
            {health ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-gray-800 p-4 rounded-lg">
                  <div className="text-sm text-gray-400 mb-1">Overall Status</div>
                  <div className={`text-lg font-semibold ${getHealthStatusColor(health.overall)}`}>
                    {health.overall.toUpperCase()}
                  </div>
                </div>
                <div className="bg-gray-800 p-4 rounded-lg">
                  <div className="text-sm text-gray-400 mb-1">Database</div>
                  <div className={`text-lg font-semibold ${getHealthStatusColor(health.database.status)}`}>
                    {health.database.status.toUpperCase()}
                  </div>
                  {health.database.details && (
                    <div className="text-xs text-gray-500">
                      {health.database.details}
                    </div>
                  )}
                </div>
                <div className="bg-gray-800 p-4 rounded-lg">
                  <div className="text-sm text-gray-400 mb-1">Redis Cache</div>
                  <div className={`text-lg font-semibold ${getHealthStatusColor(health.redis.status)}`}>
                    {health.redis.status.toUpperCase()}
                  </div>
                  {health.redis.details && (
                    <div className="text-xs text-gray-500">
                      {health.redis.details}
                    </div>
                  )}
                </div>
                <div className="bg-gray-800 p-4 rounded-lg">
                  <div className="text-sm text-gray-400 mb-1">DigitalOcean API</div>
                  <div className={`text-lg font-semibold ${getHealthStatusColor(health.digitalocean.status)}`}>
                    {health.digitalocean.status.toUpperCase()}
                  </div>
                  {health.digitalocean.details && (
                    <div className="text-xs text-gray-500">
                      {health.digitalocean.details}
                    </div>
                  )}
                </div>
                <div className="bg-gray-800 p-4 rounded-lg">
                  <div className="text-sm text-gray-400 mb-1">Celery Workers</div>
                  <div className={`text-lg font-semibold ${getHealthStatusColor(health.background_workers.status)}`}>
                    {health.background_workers.status.toUpperCase()}
                  </div>
                  {health.background_workers.details && (
                    <div className="text-xs text-gray-500">
                      {health.background_workers.details}
                    </div>
                  )}
                </div>
                {health.celery_beat && (
                  <div className="bg-gray-800 p-4 rounded-lg">
                    <div className="text-sm text-gray-400 mb-1">Celery Beat</div>
                    <div className={`text-lg font-semibold ${getHealthStatusColor(health.celery_beat.status)}`}>
                      {health.celery_beat.status.toUpperCase()}
                    </div>
                    {health.celery_beat.details && (
                      <div className="text-xs text-gray-500">
                        {health.celery_beat.details}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-gray-400">Health data unavailable</div>
            )}
          </Card>
        </div>

        {/* System Metrics */}
        <div className="mb-8">
          <Card className="p-6">
            <h2 className="text-xl font-semibold text-white mb-4">
              System Metrics ({metricsHours}h)
            </h2>
            {metrics ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-blue-400">
                    {metrics.user_registrations}
                  </div>
                  <div className="text-sm text-gray-400 mt-1">
                    New Users
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-400">
                    {metrics.new_runs}
                  </div>
                  <div className="text-sm text-gray-400 mt-1">
                    New Lab Runs
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-yellow-400">
                    {metrics.active_runs}
                  </div>
                  <div className="text-sm text-gray-400 mt-1">
                    Active Runs
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-purple-400">
                    {metrics.new_grades}
                  </div>
                  <div className="text-sm text-gray-400 mt-1">
                    New Grades
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-gray-400">Metrics data unavailable</div>
            )}
          </Card>
        </div>

        {/* Error Events Alert */}
        {metrics && metrics.error_events > 0 && (
          <div className="mb-8">
            <Alert
              variant="warning"
              title="System Alerts"
            >
              {metrics.error_events} error events detected in the last {metricsHours} hours. 
              Check the audit logs below for details.
            </Alert>
          </div>
        )}

        {/* Audit Logs */}
        <div>
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-white">
                Recent Audit Logs
              </h2>
              <div className="flex gap-4">
                <select
                  value={logsHours}
                  onChange={(e) => setLogsHours(Number(e.target.value))}
                  className="bg-gray-800 border border-gray-600 rounded px-3 py-2 text-white text-sm"
                >
                  <option value={1}>Last Hour</option>
                  <option value={6}>Last 6 Hours</option>
                  <option value={24}>Last 24 Hours</option>
                  <option value={168}>Last Week</option>
                </select>
              </div>
            </div>
            
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {auditLogs && auditLogs.logs.length > 0 ? (
                auditLogs.logs.map((log) => (
                  <div key={log.id} className="flex items-start gap-3 p-3 bg-gray-800 rounded-lg">
                    <span className="text-lg">
                      {getResourceIcon(log.resource_type)}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-white font-medium text-sm">
                          {getActionDisplayName(log.action)}
                        </span>
                        <span className="text-gray-400 text-xs capitalize">
                          {log.resource_type}
                        </span>
                        {log.resource_id && (
                          <span className="text-blue-400 text-xs">
                            #{log.resource_id}
                          </span>
                        )}
                      </div>
                      <div className="text-gray-400 text-xs mb-1">
                        {log.user ? (
                          <span>
                            by <span className="text-blue-400">{log.user.username}</span>
                            <span className="text-gray-500"> ({log.user.role})</span>
                          </span>
                        ) : (
                          'System action'
                        )}
                        {log.ip_address && (
                          <span className="ml-2 text-gray-500">
                            from {log.ip_address}
                          </span>
                        )}
                      </div>
                      <div className="text-gray-500 text-xs">
                        {formatTimestamp(log.created_at)}
                      </div>
                      {log.details && Object.keys(log.details).length > 0 && (
                        <div className="text-xs text-gray-500 mt-2 font-mono bg-gray-700 p-2 rounded">
                          {JSON.stringify(log.details, null, 2)}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-400">
                  No audit logs found for the selected time period
                </div>
              )}
            </div>

            {auditLogs && auditLogs.has_more && (
              <div className="mt-4 text-center">
                <Button 
                  variant="secondary" 
                  onClick={() => setLogsLimit(prev => prev + 50)}
                >
                  Load More Logs ({auditLogs.total_count - auditLogs.logs.length} remaining)
                </Button>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
